import axios from "axios";

const rawUrl = (process.env.REACT_APP_API_URL || process.env.REACT_APP_BACKEND_URL || "").trim();
const cleanedUrl = (() => {
  if (!rawUrl) return "";
  let u = rawUrl.split(/\s+|%20/)[0].trim().replace(/^["'(\[]+|["')\]]+$/g, "");
  const match = u.match(/https?:\/\/[^\s"'<>]+/);
  if (match) u = match[0];
  return u.replace(/\/+$/, "");
})();

const defaultBackendUrl = "https://velpula-mess-api-826716276634.asia-south1.run.app";
const isLocalhost = typeof window !== "undefined" && (window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1");
const backendUrl = isLocalhost
  ? (cleanedUrl || "http://127.0.0.1:8000")
  : (cleanedUrl && !cleanedUrl.includes("127.0.0.1") && !cleanedUrl.includes("localhost") ? cleanedUrl : defaultBackendUrl);
export const API = `${backendUrl}/api`;

export function resolveImageUrl(url) {
  if (!url) return null;
  if (url.startsWith("http://") || url.startsWith("https://") || url.startsWith("data:")) {
    return url;
  }
  let finalUrl = url;
  if (finalUrl.startsWith("/menu/") && finalUrl.endsWith(".jpg")) {
    finalUrl = finalUrl.replace(/\.jpg$/, ".webp");
  } else if (finalUrl === "/logo.png") {
    finalUrl = "/logo.webp";
  } else if (finalUrl === "/velpula_hero.jpg") {
    finalUrl = "/velpula_hero.webp";
  }
  if (finalUrl.startsWith("/menu/") || finalUrl.startsWith("/velpula_hero.") || finalUrl.startsWith("/logo.") || finalUrl.startsWith("/favicon.ico")) {
    return finalUrl;
  }
  return `${backendUrl}${finalUrl.startsWith("/") ? "" : "/"}${finalUrl}`;
}

export const api = axios.create({
  baseURL: API,
  withCredentials: true,
  timeout: 25000, // 25s timeout for resilient connections & cold starts
});

function getCookie(name) {
  const match = document.cookie.match(new RegExp("(?:^|; )" + name + "=([^;]*)"));
  return match ? decodeURIComponent(match[1]) : null;
}

const MUTATING_METHODS = new Set(["post", "put", "patch", "delete"]);

// Request Interceptor: Attach Staff or Customer Auth Tokens
api.interceptors.request.use((config) => {
  const staffToken = localStorage.getItem("gh_auth_token");
  const customerToken = localStorage.getItem("gh_customer_token");

  config.headers = config.headers || {};

  if (staffToken) {
    config.headers["Authorization"] = `Bearer ${staffToken}`;
  } else if (customerToken) {
    config.headers["Authorization"] = `Bearer ${customerToken}`;
  }

  if (customerToken) {
    config.headers["X-Customer-Token"] = `Bearer ${customerToken}`;
  }

  if (MUTATING_METHODS.has((config.method || "").toLowerCase())) {
    const csrfToken = getCookie("csrf_token") || localStorage.getItem("gh_csrf_token");
    if (csrfToken) {
      config.headers["X-CSRF-Token"] = csrfToken;
    }
  }
  return config;
});

// Response Interceptor: Automatic Retry for Network Errors and Cloud Run Cold Starts (502, 503, 504)
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const config = error.config;
    if (!config) return Promise.reject(error);

    // Initialize retry count
    config.__retryCount = config.__retryCount || 0;
    const maxRetries = 3;

    // Check if error is network-related, timeout, or server-unavailable (502/503/504)
    const isNetworkError = !error.response || error.code === "ECONNABORTED" || error.message === "Network Error";
    const isServerWarming = error.response && [502, 503, 504].includes(error.response.status);

    if (["get", "head", "options"].includes((config.method || "get").toLowerCase()) && (isNetworkError || isServerWarming) && config.__retryCount < maxRetries) {
      config.__retryCount += 1;
      const delay = Math.min(1000 * Math.pow(1.5, config.__retryCount), 3000);
      console.warn(`[API] Network retry attempt ${config.__retryCount}/${maxRetries} in ${delay}ms for: ${config.url}`);
      await new Promise((resolve) => setTimeout(resolve, delay));
      return api(config);
    }

    // Handle 401 Unauthorized / Token Expiry
    if (error.response && error.response.status === 401) {
      const detail = error.response.data?.detail || "";
      const isAuthUrl = config.url?.includes("/auth/login") || config.url?.includes("/customer/login") || config.url?.includes("/customer/register");

      if (!isAuthUrl && (detail.includes("expired") || detail.includes("revoked") || detail.includes("Not authenticated"))) {
        const staffToken = localStorage.getItem("gh_auth_token");
        const customerToken = localStorage.getItem("gh_customer_token");

        if (staffToken && (window.location.pathname.startsWith("/admin") || window.location.pathname.startsWith("/kitchen") || window.location.pathname.startsWith("/delivery"))) {
          localStorage.removeItem("gh_auth_token");
          // Stale session on staff terminal: redirect to login
          if (!window.location.pathname.includes("/login")) {
            setTimeout(() => {
              window.location.href = "/login";
            }, 1000);
          }
        } else if (customerToken) {
          localStorage.removeItem("gh_customer_token");
        }
      }
    }

    return Promise.reject(error);
  }
);

export function errMsg(e) {
  if (!e) return "Something went wrong";
  
  const d = e?.response?.data?.detail || e?.response?.data?.message;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((x) => x?.msg || JSON.stringify(x)).join(" ");

  if (e?.code === "ECONNABORTED" || e?.message === "Network Error") {
    return "Server connection is slow or reconnecting. Please check your connection or tap again.";
  }
  if (e?.response && [502, 503, 504].includes(e.response.status)) {
    return "Server is starting up. Please try again in a few seconds.";
  }

  return e?.message || "Something went wrong";
}

export async function openPdf(path, { download } = {}) {
  const res = await api.get(path, { responseType: "blob" });
  const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
  if (download) {
    const a = document.createElement("a");
    a.href = url;
    a.download = download;
    a.click();
  } else {
    window.open(url, "_blank");
  }
  setTimeout(() => URL.revokeObjectURL(url), 60000);
}

export const money = (n) =>
  `₹${Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
